/**
 * Lightweight lodash replacement
 * Only implements the functions actually used in this project
 */

// Type checks
export const isArray = Array.isArray;
export const isObject = (val) => val !== null && typeof val === 'object' && !Array.isArray(val);
export const isFunction = (val) => typeof val === 'function';

// String utilities
export const endsWith = (str, suffix) => str?.endsWith?.(suffix) ?? false;

export const deburr = (str) => {
  if (typeof str !== 'string') return '';
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

export const kebabCase = (str) => {
  if (typeof str !== 'string') return '';
  return str
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/[\s_]+/g, '-')
    .toLowerCase();
};

// Object utilities
export const get = (obj, path, defaultValue = undefined) => {
  if (obj == null) return defaultValue;
  const keys = Array.isArray(path) ? path : String(path).split('.');
  let result = obj;
  for (const key of keys) {
    if (result == null) return defaultValue;
    result = result[key];
  }
  return result === undefined ? defaultValue : result;
};

export const set = (obj, path, value) => {
  if (obj == null) return obj;
  const keys = Array.isArray(path) ? path : String(path).split('.');
  let current = obj;
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (current[key] == null) {
      current[key] = typeof keys[i + 1] === 'number' ? [] : {};
    }
    current = current[key];
  }
  current[keys[keys.length - 1]] = value;
  return obj;
};

export const result = (obj, path, defaultValue = undefined) => {
  const value = get(obj, path, defaultValue);
  return isFunction(value) ? value() : value;
};

export const omit = (obj, keys) => {
  if (obj == null) return {};
  const keysToOmit = new Set(Array.isArray(keys) ? keys : [keys]);
  return Object.fromEntries(
    Object.entries(obj).filter(([key]) => !keysToOmit.has(key))
  );
};

export const pick = (obj, keys) => {
  if (obj == null) return {};
  const keysToPick = new Set(Array.isArray(keys) ? keys : [keys]);
  return Object.fromEntries(
    Object.entries(obj).filter(([key]) => keysToPick.has(key))
  );
};

// Collection utilities
export const forEach = (collection, iteratee) => {
  if (collection == null) return;
  if (Array.isArray(collection)) {
    collection.forEach(iteratee);
  } else {
    Object.entries(collection).forEach(([key, value]) => iteratee(value, key, collection));
  }
};

export const map = (collection, iteratee) => {
  if (collection == null) return [];
  // If no iteratee, return values (like lodash does)
  if (iteratee === undefined) {
    return Array.isArray(collection) ? [...collection] : Object.values(collection);
  }
  if (Array.isArray(collection)) {
    return collection.map(iteratee);
  }
  return Object.entries(collection).map(([key, value]) => iteratee(value, key, collection));
};

export const mapValues = (obj, iteratee) => {
  if (obj == null) return {};
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [key, iteratee(value, key, obj)])
  );
};

export const find = (collection, predicate) => {
  if (collection == null) return undefined;
  const arr = Array.isArray(collection) ? collection : Object.values(collection);
  if (typeof predicate === 'function') {
    return arr.find(predicate);
  }
  if (isObject(predicate)) {
    return arr.find(item => 
      Object.entries(predicate).every(([key, val]) => item?.[key] === val)
    );
  }
  return arr.find(item => item === predicate);
};

export const sortBy = (collection, iteratee) => {
  if (collection == null) return [];
  const arr = Array.isArray(collection) ? [...collection] : Object.values(collection);
  const getKey = typeof iteratee === 'function' 
    ? iteratee 
    : (item) => get(item, iteratee);
  return arr.sort((a, b) => {
    const aVal = getKey(a);
    const bVal = getKey(b);
    if (aVal < bVal) return -1;
    if (aVal > bVal) return 1;
    return 0;
  });
};

export const reduce = (collection, iteratee, accumulator) => {
  if (collection == null) return accumulator;
  if (Array.isArray(collection)) {
    return accumulator === undefined 
      ? collection.reduce(iteratee)
      : collection.reduce(iteratee, accumulator);
  }
  return Object.entries(collection).reduce(
    (acc, [key, value]) => iteratee(acc, value, key, collection),
    accumulator
  );
};

export const flatten = (arr) => {
  if (!Array.isArray(arr)) return [];
  return arr.flat();
};

export const intersection = (arr1, arr2) => {
  if (!Array.isArray(arr1) || !Array.isArray(arr2)) return [];
  const set2 = new Set(arr2);
  return arr1.filter(item => set2.has(item));
};

// Function utilities
export const debounce = (func, wait) => {
  let timeoutId;
  return function(...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(this, args), wait);
  };
};

// Chain utility - provides a fluent interface
class LodashChain {
  constructor(value) {
    this._value = value;
  }
  
  map(iteratee) {
    // If no iteratee, just convert to array of values
    if (iteratee === undefined) {
      this._value = Array.isArray(this._value) ? [...this._value] : Object.values(this._value);
    } else {
      this._value = map(this._value, iteratee);
    }
    return this;
  }
  
  sortBy(iteratee) {
    this._value = sortBy(this._value, iteratee);
    return this;
  }
  
  filter(predicate) {
    this._value = Array.isArray(this._value) 
      ? this._value.filter(predicate)
      : Object.values(this._value).filter(predicate);
    return this;
  }
  
  value() {
    return this._value;
  }
}

export const chain = (value) => new LodashChain(value);

// Default export as an object with all methods (for compatibility with `import _ from`)
const _ = {
  isArray,
  isObject,
  isFunction,
  endsWith,
  deburr,
  kebabCase,
  get,
  set,
  result,
  omit,
  pick,
  forEach,
  map,
  mapValues,
  find,
  sortBy,
  reduce,
  flatten,
  intersection,
  debounce,
  chain,
};

export default _;
